import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllowedMenus,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  UpdateCampaigns,
  fetchProjectData,
  fetchInCampaignProjectData,
  creatInCampaignAssignerProject,
  deteInCampaignAssignerProject,
} from "../Services/APIService";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface UpdateCampaign {
  campaignId: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  isActive: boolean;
  mediaId: number;
  subMediumId: number;
  mediaIdentifierKey: string;
  campaignImage: string;
  modifiedBy: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  isActive: boolean;
}

interface Project {
  projectID: number;
  projectName: string;
  isActive: boolean;
}

interface Campaigns {
  campaignId: number;
  campaignName: string;
  projectList: ProjectList[];
}

interface ProjectList {
  projectId: number;
  projectName: string;
  identifierKey: string;
}

interface SelectOption {
  value: number;
  label: string;
  projectData?: any;
  uniqueIdentity?: string;
}

interface ValidationErrors {
  campaignName?: string;
  description?: string;
  mediaSourceId?: string;
  subMediaSourceId?: string;
  fromDate?: string;
  toDate?: string;
  campaignImage?: string;
  listOfProjects?: string;
  uniqueIdentity?: string;
}

interface Assignment {
  id: number;
  project: SelectOption;
  uniqueIdentity: string;
  projectId: string | number;
  projectName: string;
}

const EditCampaign: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [mediaSource, setMediaSource] = useState<MediaSource[]>([]);
  const [subMediaSource, setSubMediaSource] = useState<SubMediaSource[]>([]);
  const [campaignImageFile, setCampaignImageFile] = useState<File | null>(null);
  const [campaignImageChanged, setCampaignImageChanged] = useState(false);
  const [invalidFile, setInvalidFile] = useState<boolean>(false);
  const [allProjects, SetAllProjects] = useState<Project[]>([]);
  const [allInCampaignProject, SetInCampaignProject] = useState<Campaigns[]>(
    []
  );
  const [ProjectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [selectedProject, setSelectedProjects] = useState<SelectOption[]>([]);
  const [initialSelectedProjects, setInitialSelectedProjects] = useState<
    SelectOption[]
  >([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [uniqueIdentity, setUniqueIdentity] = useState("");
  const [formData, setFormData] = useState({
    campaignName: "",
    mediaSourceId: "",
    subMediaSourceId: "",
    fromDate: "",
    toDate: "",
    description: "",
    campaignImage: "",
  });
  const [assignments, setAssignments] = useState<Assignment[]>([]);

  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;

        if (menuID === undefined || menuID === null || !menuID) {
          // Get current URL
          const currentUrl = window.location.pathname;

          try {
            // Fetch allowed menus
            const menuList = await fetchAllowedMenus();

            // Find the menuID that matches the current URL
            let foundMenuID: number | null = null;

            if (menuList && menuList.length > 0) {
              // Loop through each menu and its submenus
              for (const menu of menuList) {
                if (menu.subMenuList && Array.isArray(menu.subMenuList)) {
                  for (const subMenu of menu.subMenuList) {
                    // Type assertion since we know the structure but interface doesn't define it
                    const subMenuItem = subMenu as any;
                    if (subMenuItem.subMenuUrl === currentUrl) {
                      foundMenuID = menu.menuID;
                      localStorage.setItem(
                        "activeMenuID",
                        foundMenuID.toString()
                      );
                      break;
                    }
                  }
                }
                if (foundMenuID) break;
              }
            }

            if (foundMenuID !== null) {
              localStorage.setItem("activeMenuID", foundMenuID.toString()); // Convert number to string
              console.log("Found and stored menuID:", foundMenuID);
            } else {
              localStorage.removeItem("activeMenuID");
            }
          } catch (error) {
            console.error("Error fetching allowed menus:", error);
          }
        } else {
          // Original logic for when menuID is defined
          const previousMenuID = localStorage.getItem("activeMenuID");
          const menuIDString = menuID.toString(); // Convert number to string
          if (menuIDString !== previousMenuID || menuID === null) {
            localStorage.setItem("activeMenuID", menuIDString);
          } else {
            localStorage.removeItem("activeMenuID");
          }
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingProjects(true);
      try {
        const [
          mediaSourceResponse,
          subMediaSourceResponse,
          allProjectsResponse,
          allInCampaign,
        ] = await Promise.all([
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
          fetchProjectData(),
          fetchInCampaignProjectData(),
        ]);

        setMediaSource(mediaSourceResponse || []);
        setSubMediaSource(subMediaSourceResponse || []);
        SetAllProjects(allProjectsResponse || []);
        SetInCampaignProject(allInCampaign || []);

        // Process project options
        const options = allProjectsResponse.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOptions(options);

        const campaign = location.state?.campaignData;
        // Find projects for the current campaign
        if (campaign && allInCampaign.length > 0) {
          const campaignData = allInCampaign.find(
            (c) => c.campaignId === campaign.campaignId
          );

          if (campaignData && campaignData.projectList) {
            // Type-safe filtering of project options
            const selected = options.filter((option) =>
              campaignData.projectList.some(
                (proj) => proj.projectId === option.value
              )
            );

            setSelectedProjects(selected);
            setInitialSelectedProjects(selected);
            if (
              campaignData &&
              campaignData.projectList &&
              campaignData.projectList.length > 0
            ) {
              const existingAssignments: Assignment[] =
                campaignData.projectList.map((proj: any, index: number) => ({
                  id: Date.now() + index, // Generate unique IDs
                  project: {
                    value: proj.projectId,
                    label: proj.projectName,
                  } as SelectOption,
                  uniqueIdentity:
                    proj.identifierKey ||
                    `${proj.projectName}-${proj.projectId}`, // Use identifierKey or generate one
                  projectId: proj.projectId,
                  projectName: proj.projectName,
                }));

              setAssignments(existingAssignments);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchData();
  }, [location.state]);

  useEffect(() => {
    const campaign = location.state?.campaignData;
    if (campaign) {
      setFormData((prev) => ({
        ...prev,
        campaignName: campaign.campaignName || "",
        mediaSourceId: campaign.mediaId?.toString() || "",
        subMediaSourceId: "",
        fromDate: campaign.fromDate || "",
        toDate: campaign.toDate || "",
        description: campaign.description || "",
        campaignImage: campaign.campaignImage || "",
      }));

      if (subMediaSource.length > 0 && campaign.mediaId) {
        const validSubMedia = subMediaSource.find(
          (sub) =>
            sub.mediaSourceID === parseInt(campaign.mediaId) &&
            sub.id === campaign.subMediumId
        );
        if (validSubMedia) {
          setFormData((prev) => ({
            ...prev,
            subMediaSourceId: campaign.subMediumId?.toString() || "",
          }));
        }
      }
    }
  }, [location.state, subMediaSource]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    if (value.startsWith(" ")) return;

    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: value };
      if (name === "mediaSourceId") updatedFormData.subMediaSourceId = "";
      return updatedFormData;
    });

    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const validateFileExtension = (fileName: string): boolean => {
    const validExtensions = [".jpg", ".jpeg", ".png"];
    const fileExtension = fileName
      .slice(fileName.lastIndexOf("."))
      .toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      const isValidExtension = validateFileExtension(file.name);

      if (!isValidExtension) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: "Only .jpeg, .jpg, and .png files are allowed",
        }));
        setInvalidFile(true);
        setCampaignImageChanged(true);
        setCampaignImageFile(file);
      } else {
        setCampaignImageFile(file);
        setCampaignImageChanged(true);
        setInvalidFile(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: undefined,
        }));
      }
    } else {
      setCampaignImageFile(null);
      setInvalidFile(false);
      setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
    }
  };

  // const handleRemoveImage = () => {
  //   setCampaignImageFile(null);
  //   setCampaignImageChanged(false);
  //   setInvalidFile(false);
  //   setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
  //   const fileInput = document.getElementById(
  //     "campaignImageInput"
  //   ) as HTMLInputElement;
  //   if (fileInput) {
  //     fileInput.value = "";
  //   }
  // };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.campaignName.trim()) {
      newErrors.campaignName = "Campaign name is required";
      isValid = false;
    }
    if (!formData.mediaSourceId) {
      newErrors.mediaSourceId = "Media Source is required";
      isValid = false;
    }
    if (!formData.subMediaSourceId) {
      newErrors.subMediaSourceId = "Sub Media Source is required";
      isValid = false;
    }
    if (!formData.fromDate) {
      newErrors.fromDate = "From Date is required";
      isValid = false;
    }
    if (!formData.toDate) {
      newErrors.toDate = "To Date is required";
      isValid = false;
    }
    if (formData.fromDate && formData.toDate) {
      const fromDateObj = new Date(formData.fromDate);
      const toDateObj = new Date(formData.toDate);
      if (toDateObj < fromDateObj) {
        newErrors.toDate = "To Date cannot be earlier than From Date";
        isValid = false;
      }
    }
    if (invalidFile) {
      newErrors.campaignImage = "Only .jpeg, .jpg, and .png files are allowed";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedProject || selectedProject.length === 0) {
      newErrors.listOfProjects = "Please select at least one project";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    const userId = localStorage.getItem("UserId");

    let campaignImageData = "";
    if (campaignImageFile) {
      try {
        campaignImageData = await fileToBase64(campaignImageFile);
      } catch (error) {
        console.error("Error converting campaign image to Base64:", error);
        toast.error("Failed to process the campaign image");
        return;
      }
    }

    try {
      let campaignImageData = formData.campaignImage;
      const campaign = location.state?.campaignData;

      if (campaignImageChanged && campaignImageFile) {
        campaignImageData = await fileToBase64(campaignImageFile);
      }

      if (!campaign) {
        toast.error("Campaign data not found");
        return;
      }

      const updateData: UpdateCampaign = {
        campaignId: campaign.campaignId,
        campaignName: formData.campaignName,
        description: formData.description,
        fromDate: new Date(formData.fromDate),
        toDate: new Date(formData.toDate),
        isActive: campaign.isActive || true,
        mediaId: parseInt(formData.mediaSourceId),
        subMediumId: parseInt(formData.subMediaSourceId || "0"),
        mediaIdentifierKey: "224f8774-d7c4-45a2-b9da-4bd2d350ac68",
        campaignImage: campaignImageData,
        modifiedBy: userId ? parseInt(userId) : 0,
      };

      const result = await UpdateCampaigns(updateData);

      if (result.isUpdated) {
        toast.success(result.message || "Campaign updated successfully");
        setTimeout(() => {
          navigate("/campaignView");
        }, 1000);
      } else {
        toast.error(result.message || "Failed to update campaign");
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast.error("An error occurred while updating the campaign");
    }
  };

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedProjects([]);
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedProjects(selectedOptions);

    if (errors.listOfProjects) {
      setErrors({
        ...errors,
        listOfProjects: undefined,
      });
    }
  };

  const handleSingleSelectChange = (selectedOption: SelectOption | null) => {
    if (!selectedOption) return;
    setSelectedProjects([selectedOption]);
  };

  const handleUniqueIdentityChange = (e: any) => {
    setUniqueIdentity(e.target.value);
    // Clear any existing error for this field
    if (errors.uniqueIdentity) {
      setErrors((prev) => ({
        ...prev,
        uniqueIdentity: "",
      }));
    }
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignments || assignments.length === 0) {
      toast.error("Please add at least one project");
    }

    // if (!validateAssignmentData()) {
    //   toast.error("Please select at least one project");
    //   return;
    // }

    const campaign = location.state?.campaignData;

    // if (!campaign) {
    //   toast.error("Campaign data not found");
    //   return;
    // }

    try {
      setIsSubmittingAssignment(true);

      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");

      if (!tenantId || !userId) {
        throw new Error("Required session data not found");
      }

      // Get current and initial selected project IDs
      const currentSelectedProjects = selectedProject;
      const initialSelectedProjectsList = initialSelectedProjects;

      // Process projects to remove (all initial projects)
      let projectsToRemove: SelectOption[] = [];
      let projectsToAdd: SelectOption[] = [];

      // If there are initial projects, remove them all
      if (initialSelectedProjectsList.length > 0) {
        projectsToRemove = initialSelectedProjectsList;
      }

      // If there are current projects, add them all
      if (currentSelectedProjects.length > 0) {
        projectsToAdd = currentSelectedProjects;
      }

      // Process removals
      if (projectsToRemove.length > 0) {
        const deleteData = {
          tenantId: parseInt(tenantId),
          campaignId: campaign.campaignId,
          projectList: projectsToRemove.map((project) => ({
            projectId: project.value,
          })),
        };

        const removeResult = await deteInCampaignAssignerProject(deleteData);

        if (!removeResult.isUpdated) {
          throw new Error(
            removeResult.message || "Failed to unassign projects"
          );
        }
      }

      // Process additions
      if (assignments.length > 0) {
        const createData = {
          tenantId: parseInt(tenantId),
          campaignId: campaign.campaignId,
          projectList: assignments.map((assign) => ({
            projectId: parseInt(assign.projectId.toString()),
            identifierKey: assign.uniqueIdentity.trim(),
          })),
          createdBy: parseInt(userId),
        };
        console.log("createData", createData);

        const addResult = await creatInCampaignAssignerProject(createData);

        if (!addResult.isCreated) {
          throw new Error(addResult.message || "Failed to assign new projects");
        }
        // Show success message if any changes were made
        if (addResult.isCreated) {
          toast.success("Campaign Project updated successfully");
          setInitialSelectedProjects([...currentSelectedProjects]);
          setActiveTab("home");
        } else {
          toast.error("No changes in project assignments");
        }
      }
    } catch (error) {
      console.error("Error updating project assignments:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while updating project assignments"
      );
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const handleAddAssignment = () => {
    console.log("selectedProject", selectedProject);
    console.log("uniqueIdentity", uniqueIdentity);

    if (!selectedProject || !uniqueIdentity.trim()) {
      // Set appropriate errors
      setErrors((prev) => ({
        ...prev,
        listOfProjects: !selectedProject ? "Please select a project" : "",
        uniqueIdentity: !uniqueIdentity.trim()
          ? "Please enter unique identity"
          : "",
      }));
      return;
    }

    const existsInAssignments = assignments.some(
      (assignment) =>
        assignment.uniqueIdentity.toLowerCase() === uniqueIdentity.toLowerCase()
    );

    if (existsInAssignments) {
      setErrors((prev) => ({
        ...prev,
        uniqueIdentity: "This unique identity already exists",
      }));
      return;
    }

    const newAssignment: Assignment = {
      id: Date.now(), // Simple ID generation, you might want to use a better method
      project: selectedProject[0], // Store only the selected project object
      uniqueIdentity: uniqueIdentity,
      projectId: selectedProject[0].value, // Access first element's value
      projectName: selectedProject[0].label, // Access first element's label
    };

    setAssignments((prev) => [...prev, newAssignment]);

    setSelectedProjects([]);
    setUniqueIdentity("");

    setErrors((prev) => ({
      ...prev,
      listOfProjects: "",
      uniqueIdentity: "",
    }));
  };

  const handleRemoveAssignment = (assignmentId: any) => {
    setAssignments((prev) =>
      prev.filter((assignment) => assignment.id !== assignmentId)
    );
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  // const iconButtonStyle: React.CSSProperties = {
  //   top: "36%",
  //   right: "10px",
  //   transform: "translateY(-50%)",
  //   background: "none",
  //   border: "none",
  //   fontSize: "16px",
  //   lineHeight: "1",
  //   color: "#495057",
  //   position: "absolute",
  // };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <ul
                className="nav nav-tabs tabHeaderPadding cardHeaderHegiht"
                id="myTab"
                role="tablist"
                style={{ borderBottom: "1px solid #84556b" }}
              >
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "home" ? "active" : ""
                    }`}
                    id="home-tab"
                    onClick={() => handleTabClick("home")}
                    type="button"
                    role="tab"
                    aria-controls="home-tab-pane"
                    aria-selected={activeTab === "home"}
                  >
                    Campaign Details
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "contact" ? "active" : ""
                    }`}
                    id="contact-tab"
                    onClick={() => handleTabClick("contact")}
                    type="button"
                    role="tab"
                    aria-controls="contact-tab-pane"
                    aria-selected={activeTab === "contact"}
                  >
                    Assigners Information
                  </button>
                </li>
              </ul>

              <div className="tab-content card-body" id="myTabContent">
                <div
                  className={`tab-pane fade ${
                    activeTab === "home" ? "show active" : ""
                  }`}
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Campaign Name{requiredField}
                        </label>
                        <input
                          name="campaignName"
                          placeholder="Campaign Name"
                          value={formData.campaignName}
                          onChange={handleChange}
                          className={`form-control ${
                            errors.campaignName ? "is-invalid" : ""
                          }`}
                        />
                        {errors.campaignName && (
                          <div className="invalid-feedback text-danger">
                            {errors.campaignName}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Media Source{requiredField}
                        </label>
                        {isLoadingProjects ? (
                          <div className="text-muted">
                            Loading Media Sources...
                          </div>
                        ) : (
                          <select
                            name="mediaSourceId"
                            value={formData.mediaSourceId || ""}
                            onChange={handleChange}
                            className={`form-select ${
                              errors.mediaSourceId ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select Media Source</option>
                            {mediaSource.map((MS) => (
                              <option key={MS.sourceId} value={MS.sourceId}>
                                {MS.sourceName}
                              </option>
                            ))}
                          </select>
                        )}
                        {errors.mediaSourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.mediaSourceId}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Sub Media Source{requiredField}
                        </label>
                        {isLoadingProjects ? (
                          <div className="text-muted">
                            Loading Sub Media Sources...
                          </div>
                        ) : (
                          <select
                            name="subMediaSourceId"
                            value={formData.subMediaSourceId || ""}
                            onChange={handleChange}
                            className={`form-select ${
                              errors.subMediaSourceId ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select Sub Media Source</option>
                            {subMediaSource
                              .filter(
                                (sub) =>
                                  sub.mediaSourceID ===
                                  parseInt(formData.mediaSourceId)
                              )
                              .map((sub) => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.name}
                                </option>
                              ))}
                          </select>
                        )}
                        {errors.subMediaSourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.subMediaSourceId}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          From Date{requiredField}
                        </label>
                        <input
                          type="datetime-local"
                          name="fromDate"
                          value={formData.fromDate}
                          onChange={handleChange}
                          className={`form-control form-control-lg ${
                            errors.fromDate ? "is-invalid" : ""
                          }`}
                        />
                        {errors.fromDate && (
                          <div className="invalid-feedback text-danger">
                            {errors.fromDate}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          To Date{requiredField}
                        </label>
                        <input
                          type="datetime-local"
                          name="toDate"
                          value={formData.toDate}
                          onChange={handleChange}
                          className={`form-control form-control-lg ${
                            errors.toDate ? "is-invalid" : ""
                          }`}
                        />
                        {errors.toDate && (
                          <div className="invalid-feedback text-danger">
                            {errors.toDate}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">Campaign Image</label>
                        <div className="position-relative">
                          <input
                            id="campaignImageInput"
                            type="file"
                            className={`form-control form-control-lg ${
                              errors.campaignImage ? "is-invalid" : ""
                            }`}
                            onChange={handleFileChange}
                            accept="image/jpeg,image/jpg,image/png"
                            style={{
                              paddingRight: campaignImageFile ? "30px" : "12px",
                            }}
                          />
                          {formData.campaignImage &&
                            !campaignImageChanged &&
                            !campaignImageFile && (
                              <div className="form-text text-success">
                                Campaign Image already uploaded
                              </div>
                            )}
                          {campaignImageFile && (
                            <>
                              <div className="form-text">
                                Selected: {campaignImageFile.name}
                              </div>
                              {/* <button
                                type="button"
                                className="position-absolute p-0"
                                style={iconButtonStyle}
                                onClick={handleRemoveImage}
                              >
                                ×
                              </button> */}
                            </>
                          )}
                        </div>
                        {errors.campaignImage && (
                          <div className="invalid-feedback text-danger d-block">
                            {errors.campaignImage}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          placeholder="Description"
                          value={formData.description}
                          onChange={handleChange}
                          className={`form-control ${
                            errors.description ? "is-invalid" : ""
                          }`}
                        />
                        {errors.description && (
                          <div className="invalid-feedback text-danger">
                            {errors.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                <div
                  className={`tab-pane fade ${
                    activeTab === "contact" ? "show active" : ""
                  }`}
                  id="contact-tab-pane"
                  role="tabpanel"
                  aria-labelledby="contact-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleAssignmentSubmit}>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Assign Project{requiredField}
                        </label>
                        <Select
                          options={ProjectOptions}
                          value={selectedProject}
                          onChange={handleSingleSelectChange}
                          className={`basic-multi-select ${
                            errors.listOfProjects ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select project to campaign"
                          isLoading={isLoadingProjects}
                          noOptionsMessage={() => "No project available"}
                        />
                        {errors.listOfProjects && (
                          <div className="text-danger mt-1 small">
                            {errors.listOfProjects}
                          </div>
                        )}
                        <div className="form-text">
                          Select multiple project to assign to this campaign
                        </div>
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">
                          Unique Identity{requiredField}
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.uniqueIdentity ? "is-invalid" : ""
                          }`}
                          value={uniqueIdentity}
                          onChange={handleUniqueIdentityChange}
                          placeholder="Enter unique identity"
                          maxLength={50}
                        />
                        {errors.uniqueIdentity && (
                          <div className="text-danger mt-1 small">
                            {errors.uniqueIdentity}
                          </div>
                        )}
                        <div className="form-text">
                          Enter a unique identifier for this assignment
                        </div>
                      </div>
                      <div className="col-12 col-lg-2 mb-3 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={handleAddAssignment}
                        >
                          <i className="bi bi-plus-circle me-1"></i>
                          Add
                        </button>
                      </div>
                    </div>
                  </form>

                  {assignments.length > 0 && (
                    <div className="mt-4">
                      <h6 className="mb-3">Project Assignments</h6>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Project ID</th>
                              <th scope="col">Project Name</th>
                              <th scope="col">Unique Identity</th>
                              <th scope="col" className="text-center">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignments.map((assignment, index) => (
                              <tr key={assignment.id}>
                                <td>{index + 1}</td>
                                <td>
                                  <span>{assignment.projectId}</span>
                                </td>
                                <td>{assignment.projectName}</td>
                                <td>
                                  <span className="fw-semibold">
                                    {assignment.uniqueIdentity}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() =>
                                      handleRemoveAssignment(assignment.id)
                                    }
                                    title="Remove assignment"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-muted small mb-4">
                        Total assignments: {assignments.length}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  {activeTab !== "contact" ? (
                    <>
                      <button
                        type="submit"
                        className="btn btn-primary me-2"
                        onClick={handleSubmit}
                      >
                        Update Campaign
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/campaignView")}
                      >
                        <i className="bi bi-arrow-return-left"></i> Back To List
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary me-2"
                        onClick={handleAssignmentSubmit}
                      >
                        Save Assigner
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/campaignView")}
                      >
                        <i className="bi bi-arrow-return-left"></i> Back To List
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default EditCampaign;
